import React from 'react'
import styled, {css} from 'styled-components'
import {DeltaIndicator, DeltaIndicatorSpacer, type DeltaIndicatorProps} from './DeltaIndicator'
import {InfoHint} from './InfoHint'
import {DEFAULT_ERROR_LABEL, isBlankNode, StatValue, statValueState} from './internal/StatValue'

/**
 * StatGrid
 *
 * A grid of figures, each an uppercase caption over a navy value, optionally with a delta line
 * under it. A `<dl>`: one `div` per figure holding the caption (`dt`), the value (`dd`) and, when
 * there is one, the delta (a second `dd`).
 *
 * - `variant="divided"` is one bordered block cut into cells by hairlines (the KPI grid, mock
 *   .ep-kpis). `variant="plain"` (default) is a gap grid with no chrome (the header facts, the call
 *   activity pairs).
 * - `columns` is the count at full width. The grid steps down by its own width, not the
 *   viewport's (a container query), so it reflows the same beside the sidebar and full width:
 *   STAT_GRID_STEPS lists the steps.
 * - `size` sets the value: sm body (mock .ep-mv), md h3 (.ep-pv), lg h2 (.ep-kv).
 * - `surface="tinted"` is for a grid on a tinted card (DashboardCard `surface="tinted"`, the
 *   Financial card): --text-secondary is 4.17:1 on --bg-selected, so the captions and every
 *   secondary-ink text inside the grid take --text-primary.
 * - Each figure has its own state, by the shared precedence loading > error > pending > empty >
 *   value (see StatValue). A failed figure reads 'Couldn’t load', never '—'. The grid's own
 *   `loading` / `error` apply to every figure; while any figure or delta loads the list is
 *   aria-busy.
 * - A figure's `hint` is an InfoHint beside its caption (`${cellTestId}-info`). A figure reading
 *   '—' with an `emptyReason`, or its pending text with a `pendingReason`, explains itself through
 *   that same trigger, the reason added to the hint; a figure with no hint makes the '—' or the
 *   pending text itself the trigger. One trigger per figure either way. A failure's reason has no
 *   slot of its own: put it in the `hint`.
 * - `delta` renders a DeltaIndicator line (`${cellTestId}-delta`) under the value, in every state:
 *   a skeleton while the figure loads, and the '—' trigger with the error text once it has failed
 *   (a change can't be read off a figure that couldn't be). `noDelta` renders an empty line of the
 *   same height, hidden and without a test id, for a figure with no delta among figures that have
 *   one.
 *
 * Test ids: the grid's `data-testid` is on the `<dl>`, which also carries `data-columns`. Each
 * figure has a base id, `${testId}-${id}` unless the item names its own; its value (`dd`) carries
 * it, its caption is `-label`, its hint `-info` and its delta `-delta`. `valueTestId` gives the
 * value a different id, or none (null), e.g. when the value is a control that carries the base id
 * itself.
 *
 * @example
 *   <StatGrid variant="divided" columns={4} size="lg" aria-label="Performance"
 *     data-testid="staffer-metrics-performance"
 *     items={[
 *       {id: 'total-fills', label: 'Total fills', value: '173', hint: definition,
 *        delta: {text: '+12 placed', direction: 'up', sentiment: 'positive'},
 *        'data-testid': 'staffer-metrics-tile-total-fills'},
 *       {id: 'active', label: 'Active orders', value: '5', hint: asOfNow, noDelta: true,
 *        'data-testid': 'staffer-metrics-tile-active'},
 *     ]} />
 *   <StatGrid columns={2} size="md" data-testid="staffer-metrics-activity"
 *     items={[{id: 'connect-rate', label: 'Connect rate', pending: 'Reconnect Zoom',
 *              pendingReason: whyReconnect}]} />
 */

export type StatGridColumns = 2 | 4 | 5
export type StatGridVariant = 'divided' | 'plain'
export type StatGridSize = 'sm' | 'md' | 'lg'
/** The surface the grid sits on; the same values as DashboardCard's `surface`. */
export type StatGridSurface = 'default' | 'tinted'

export interface StatGridStep {
  /** The step applies while the grid is at most this wide, in rem. */
  maxWidthRem: number
  /** The column count at this step. */
  columns: number
}

/**
 * The column steps below each full-width count, widest first. There are no breakpoint tokens, so
 * these are rem literals. The widths are the grid's own, measured at the mock's breakpoints (MOCK
 * :505-512) as the nested content width: viewport − 2 × 40 page padding − 266 sidebar − 22 gap −
 * 2 border − 32 body padding.
 * - 4 → 2 at 53rem: 848px, the nested width at the mock's 1250px breakpoint; → 1 at 30rem, so a
 *   320px screen reflows (not in the mock).
 * - 5 → 3 at 69rem: ≈ the nested width at the mock's 1500px breakpoint; → 2 at 45rem.
 * - 2 → 1 at 30rem.
 */
export const STAT_GRID_STEPS: Readonly<Record<StatGridColumns, readonly StatGridStep[]>> = {
  2: [{maxWidthRem: 30, columns: 1}],
  4: [
    {maxWidthRem: 53, columns: 2},
    {maxWidthRem: 30, columns: 1},
  ],
  5: [
    {maxWidthRem: 69, columns: 3},
    {maxWidthRem: 45, columns: 2},
  ],
}

export interface StatGridItem {
  id: string
  /** The caption, shown in uppercase. */
  label: string
  /** The figure. null, undefined, '' and false read '—'; 0 is a figure. */
  value?: React.ReactNode
  /** The figure's InfoHint content (its definition), beside the caption. */
  hint?: React.ReactNode
  /** The hint's accessible name (default `About ${label}`). */
  hintLabel?: string
  /** The value in the mono face (money, ids). The state texts stay in the sans face. */
  mono?: boolean
  /** The change line under the value. Its test id defaults to `${cellTestId}-delta`. */
  delta?: DeltaIndicatorProps
  /** No delta, but keep the delta line's height so the figure lines up with its neighbours. */
  noDelta?: boolean
  loading?: boolean
  /** Truthy = the figure could not be read: 'Couldn’t load'. */
  error?: Error | null | boolean
  /** The error text (default "Couldn’t load"). */
  errorLabel?: string
  /** Text in place of the figure, e.g. 'Not tracked yet' or '12 pending'. */
  pending?: React.ReactNode
  /** Why the figure is '—'. */
  emptyReason?: React.ReactNode
  /** Why the figure reads its pending text, e.g. why 'Reconnect Zoom'. */
  pendingReason?: React.ReactNode
  /** The figure's base test id: on the value, and the stem of -label, -info and -delta. Default
   *  `${testId}-${id}`. */
  'data-testid'?: string
  /** The value's own test id in place of the base id; null for none (a control as the value that
   *  carries the base id itself). */
  valueTestId?: string | null
}

export interface StatGridProps {
  items: StatGridItem[]
  /** The column count at full width; fewer as the grid narrows (STAT_GRID_STEPS). Default 4. */
  columns?: StatGridColumns
  variant?: StatGridVariant
  size?: StatGridSize
  /** `'tinted'` on a --bg-selected card: primary ink for captions and secondary texts. Default
   *  `'default'`. */
  surface?: StatGridSurface
  /** Every figure is loading. */
  loading?: boolean
  /** Every figure failed. */
  error?: Error | null | boolean
  /** The error text for every figure (default "Couldn’t load"); an item's own wins. */
  errorLabel?: string
  /** Names the list. */
  'aria-label'?: string
  'data-testid'?: string
}

interface ValueStyle {
  size: string
  weight: string
}

/** sm is the header facts' text (mock .ep-mv, medium); md and lg are figures, bold. */
const VALUE: Record<StatGridSize, ValueStyle> = {
  sm: {size: 'var(--fs-body)', weight: 'var(--fw-medium)'},
  md: {size: 'var(--fs-h3)', weight: 'var(--fw-bold)'},
  lg: {size: 'var(--fs-h2)', weight: 'var(--fw-bold)'},
}

/** The divided grid's cells and filler: the surface the grid sits on. */
const CELL_FILL: Record<StatGridSurface, string> = {
  default: 'var(--bg-card)',
  tinted: 'var(--bg-selected)',
}

/** The divided grid's hairlines and border. On the tint they take its own hue, as DashboardCard's. */
const HAIRLINE: Record<StatGridSurface, string> = {
  default: 'var(--border-subtle)',
  tinted: 'var(--brand-200)',
}

/** Caption ink: --text-secondary is 4.17:1 on --bg-selected, below AA for small text. */
const CAPTION_INK: Record<StatGridSurface, string> = {
  default: 'var(--text-secondary)',
  tinted: 'var(--text-primary)',
}

/** How many columns the last row leaves empty. */
const emptyTrailingColumns = (count: number, columns: number) =>
  (columns - (count % columns)) % columns

const trackTemplate = (columns: number) => `repeat(${columns}, minmax(0, 1fr))`

/**
 * The divided grid's filler: the list's ::after, a block in the cells' fill spanning the columns
 * the last row leaves empty, so the hairline background doesn't show through as a patch. A
 * pseudo-element rather than empty cells, so the list holds only real figures.
 */
const filler = (span: number) => css`
  &::after {
    display: ${span > 0 ? 'block' : 'none'};
    grid-column: span ${Math.max(span, 1)};
  }
`

/** The base column count, then one container query per step, widest first. */
function columnRules(
  columns: StatGridColumns,
  count: number,
  divided: boolean,
  surface: StatGridSurface,
) {
  const fills = divided
    ? [columns, ...STAT_GRID_STEPS[columns].map((step) => step.columns)].some(
        (cols) => emptyTrailingColumns(count, cols) > 0,
      )
    : false
  return css`
    grid-template-columns: ${trackTemplate(columns)};
    ${fills &&
    css`
      &::after {
        content: '';
        background: ${CELL_FILL[surface]};
      }
      ${filler(emptyTrailingColumns(count, columns))}
    `}

    ${STAT_GRID_STEPS[columns].map(
      (step) => css`
        @container stat-grid (max-width: ${step.maxWidthRem}rem) {
          grid-template-columns: ${trackTemplate(step.columns)};
          ${fills && filler(emptyTrailingColumns(count, step.columns))}
        }
      `,
    )}
  `
}

/** The query container: the grid steps by this element's width. */
const Container = styled.div`
  container-type: inline-size;
  container-name: stat-grid;
  /* Inline-size containment ignores the content's width, so take the parent's (a flex row too). */
  width: 100%;
  min-width: 0;
  font-family: var(--font-sans);
`

const List = styled.dl<{
  $columns: StatGridColumns
  $count: number
  $variant: StatGridVariant
  $surface: StatGridSurface
}>`
  display: grid;
  margin: 0;
  ${({$columns, $count, $variant, $surface}) =>
    columnRules($columns, $count, $variant === 'divided', $surface)}

  ${({$variant, $surface}) =>
    $variant === 'divided'
      ? css`
          /* Hairlines: the cells sit on the border colour with a one-border gap between them. */
          gap: var(--border-thin);
          background: ${HAIRLINE[$surface]};
          border: var(--border-thin) solid ${HAIRLINE[$surface]};
          border-radius: var(--radius-md);
          overflow: hidden;
        `
      : css`
          gap: var(--space-3) var(--space-4);
        `}

  ${({$surface}) =>
    $surface === 'tinted' &&
    css`
      /* The parts this grid is built from (the '—' and pending texts, the hint triggers, a neutral
         delta) set their ink as --text-secondary; on the tint that role reads --text-primary. */
      --text-secondary: var(--text-primary);
    `}
`

const Cell = styled.div<{$variant: StatGridVariant; $surface: StatGridSurface}>`
  display: flex;
  flex-direction: column;
  min-width: 0;

  ${({$variant, $surface}) =>
    $variant === 'divided' &&
    css`
      padding: var(--space-3) var(--space-4);
      background: ${CELL_FILL[$surface]};
    `}
`

const Caption = styled.dt<{$surface: StatGridSurface}>`
  display: flex;
  align-items: center;
  gap: var(--space-1);
  min-width: 0;
  margin: 0 0 calc(var(--space-1) / 2);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  letter-spacing: var(--ls-wide);
  text-transform: uppercase;
  color: ${({$surface}) => CAPTION_INK[$surface]};
`

const Value = styled.dd<{$size: StatGridSize; $mono: boolean}>`
  min-width: 0;
  margin: 0;
  font-family: ${({$mono}) => ($mono ? 'var(--font-mono)' : 'inherit')};
  font-size: ${({$size}) => VALUE[$size].size};
  font-weight: ${({$size}) => VALUE[$size].weight};
  line-height: var(--lh-normal);
  letter-spacing: var(--ls-normal);
  font-variant-numeric: tabular-nums;
  color: var(--primary-800);
`

/** The delta's line, or the spacer's: the same element either way, so both are one height. */
const DeltaSlot = styled.dd`
  min-width: 0;
  margin: 0;
`

export function StatGrid({
  items,
  columns = 4,
  variant = 'plain',
  size = 'md',
  surface = 'default',
  loading = false,
  error,
  errorLabel,
  'aria-label': ariaLabel,
  'data-testid': testId,
}: StatGridProps) {
  const cells = items.map((item) => {
    const cellLoading = loading || Boolean(item.loading)
    const cellError = Boolean(error) || Boolean(item.error)
    return {
      item,
      cellLoading,
      cellError,
      state: statValueState({
        value: item.value,
        loading: cellLoading,
        error: cellError,
        pending: item.pending,
      }),
    }
  })
  // A delta that loads on its own (the prior window still coming) is a hidden skeleton too.
  const busy = cells.some((cell) => cell.state === 'loading' || Boolean(cell.item.delta?.loading))

  return (
    <Container>
      <List
        $columns={columns}
        $count={items.length}
        $variant={variant}
        $surface={surface}
        data-columns={columns}
        aria-label={ariaLabel}
        aria-busy={busy || undefined}
        data-testid={testId}>
        {cells.map(({item, cellLoading, cellError, state}) => {
          const cellTestId = item['data-testid'] ?? (testId ? `${testId}-${item.id}` : undefined)
          const derived = (suffix: string) => (cellTestId ? `${cellTestId}-${suffix}` : undefined)
          const valueTestId =
            item.valueTestId === undefined ? cellTestId : item.valueTestId ?? undefined
          const infoTestId = derived('info')
          const hintLabel = item.hintLabel ?? `About ${item.label}`
          const hasHint = !isBlankNode(item.hint)
          const stateReason =
            state === 'empty' ? item.emptyReason : state === 'pending' ? item.pendingReason : null
          const reason = isBlankNode(stateReason) ? null : stateReason
          // A failed figure's change can't be read either: the '—' with the error text, in place
          // of any reason meant for a figure that loaded.
          const deltaReason = cellError
            ? item.errorLabel ?? errorLabel ?? DEFAULT_ERROR_LABEL
            : item.delta?.unavailableReason
          // One trigger per figure: a figure with a hint carries its '—' or pending reason there.
          const hintContent =
            hasHint && reason !== null ? (
              <>
                {item.hint} {reason}
              </>
            ) : (
              item.hint
            )

          return (
            <Cell key={item.id} $variant={variant} $surface={surface}>
              <Caption $surface={surface} data-testid={derived('label')}>
                {item.label}
                {hasHint ? (
                  <InfoHint label={hintLabel} content={hintContent} data-testid={infoTestId} />
                ) : null}
              </Caption>
              <Value $size={size} $mono={Boolean(item.mono)} data-testid={valueTestId}>
                <StatValue
                  value={item.value}
                  loading={cellLoading}
                  error={cellError}
                  errorLabel={item.errorLabel ?? errorLabel}
                  pending={item.pending}
                  emptyReason={hasHint ? undefined : item.emptyReason}
                  pendingReason={hasHint ? undefined : item.pendingReason}
                  reasonLabel={hintLabel}
                  reasonTestId={infoTestId}
                />
              </Value>
              {item.delta ? (
                <DeltaSlot>
                  <DeltaIndicator
                    data-testid={derived('delta')}
                    {...item.delta}
                    loading={cellLoading || Boolean(item.delta.loading)}
                    unavailableReason={deltaReason}
                  />
                </DeltaSlot>
              ) : item.noDelta ? (
                <DeltaSlot aria-hidden="true">
                  <DeltaIndicatorSpacer />
                </DeltaSlot>
              ) : null}
            </Cell>
          )
        })}
      </List>
    </Container>
  )
}
